<script lang="ts">
  import { competitorServicesApi } from '@shared/api/competitor-services';
  import { blankToNull } from '@shared/util/text';
  import { normalizeThirdPartySolutionErrorMessage } from '@domain/competitor-service/third-party-solution-edit';
  import InlineField from '@shared/ui/InlineField.svelte';
  import type {
    CompetitorServiceReferralSetting,
    CompetitorServiceReferralSettingCreateRequest,
    CompetitorServiceReferralSettingPatchRequest,
  } from '@shared/types/competitor-service';

  interface Props {
    competitorServiceId: string;
    setting?: CompetitorServiceReferralSetting | null;
    canEdit?: boolean;
    onChanged: () => void | Promise<void>;
  }

  type ReferralField = 'referral_contact' | 'referral_conditions' | 'memo';

  const {
    competitorServiceId,
    setting = null,
    canEdit = false,
    onChanged,
  }: Props = $props();

  let savingField = $state<ReferralField | null>(null);

  function normalizeError(err: unknown, fallback: string): string {
    return err instanceof Error
      ? normalizeThirdPartySolutionErrorMessage(err.message)
      : fallback;
  }

  function fieldText(value: string | null): string {
    const trimmed = value?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : '未設定';
  }

  function fieldValue(field: ReferralField): string | null {
    return setting?.[field] ?? null;
  }

  async function saveField(field: ReferralField, next: string): Promise<void> {
    if (!canEdit || savingField) return;
    savingField = field;
    try {
      const payload:
        | CompetitorServiceReferralSettingCreateRequest
        | CompetitorServiceReferralSettingPatchRequest = {
        [field]: blankToNull(next),
      };
      if (setting) {
        await competitorServicesApi.patchReferralSetting(
          competitorServiceId,
          payload,
        );
      } else {
        await competitorServicesApi.createReferralSetting(
          competitorServiceId,
          payload,
        );
      }
      await onChanged();
    } catch (err) {
      throw new Error(normalizeError(err, '紹介情報の保存に失敗しました'));
    } finally {
      savingField = null;
    }
  }
</script>

<section
  class="referral-editor"
  data-testid="referral-setting-editor"
  aria-busy={savingField !== null}
>
  <header class="editor-toolbar">
    <h2>紹介情報</h2>
  </header>

  {#if setting === null && !canEdit}
    <p class="empty" data-testid="referral-setting-empty">
      紹介情報はありません。
    </p>
  {:else if canEdit}
    <div class="inline-grid" data-testid="referral-setting-inline">
      <div data-testid="referral-setting-contact-inline">
        <InlineField
          label="紹介先担当者"
          value={fieldValue('referral_contact')}
          type="text"
          placeholder="未設定"
          affordance="text"
          commitEmpty={true}
          disabled={savingField !== null && savingField !== 'referral_contact'}
          onSave={(next) => saveField('referral_contact', next)}
        />
      </div>
      <div data-testid="referral-setting-conditions-inline">
        <InlineField
          label="紹介条件"
          value={fieldValue('referral_conditions')}
          type="textarea"
          textareaRows={3}
          placeholder="未設定"
          affordance="text"
          commitEmpty={true}
          disabled={savingField !== null &&
            savingField !== 'referral_conditions'}
          onSave={(next) => saveField('referral_conditions', next)}
        />
      </div>
      <div class="wide" data-testid="referral-setting-memo-inline">
        <InlineField
          label="メモ"
          value={fieldValue('memo')}
          type="textarea"
          textareaRows={3}
          placeholder="未設定"
          affordance="text"
          commitEmpty={true}
          disabled={savingField !== null && savingField !== 'memo'}
          onSave={(next) => saveField('memo', next)}
        />
      </div>
    </div>
  {:else if setting}
    <div class="read-grid" data-testid="referral-setting-read">
      <div class="read-item">
        <span class="read-label">紹介先担当者</span>
        <span class="read-value">{fieldText(setting.referral_contact)}</span>
      </div>
      <div class="read-item">
        <span class="read-label">紹介条件</span>
        <span class="read-value">{fieldText(setting.referral_conditions)}</span>
      </div>
      <div class="read-item wide">
        <span class="read-label">メモ</span>
        <span class="read-value">{fieldText(setting.memo)}</span>
      </div>
    </div>
  {/if}
</section>

<style>
  .referral-editor {
    display: grid;
    gap: 12px;
  }
  .editor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }
  .editor-toolbar h2 {
    margin: 0;
    color: #111827;
    font-size: 1rem;
    line-height: 1.4;
  }
  .inline-grid,
  .read-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 18px;
    border-top: 1px solid #e2e8f0;
    padding-top: 12px;
  }
  .inline-grid > .wide {
    grid-column: 1 / -1;
  }
  .inline-grid :global(.inline-field) {
    gap: 4px;
  }
  .inline-grid :global(.field-label) {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }
  .inline-grid :global(.inline-display) {
    min-width: 80px;
    max-width: 100%;
    line-height: 1.6;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
  .inline-grid :global(.inline-display:hover) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
  .inline-grid :global(.inline-control) {
    width: 100%;
    box-sizing: border-box;
  }
  .read-item {
    display: grid;
    gap: 4px;
    min-width: 0;
  }
  .read-item.wide {
    grid-column: 1 / -1;
  }
  .read-label {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }
  .read-value {
    color: #1f2937;
    font-size: 13px;
    line-height: 1.6;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .empty {
    color: #64748b;
  }
  @media (max-width: 720px) {
    .inline-grid,
    .read-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
